import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { RecordingState, RecordingCompleted } from '../../@core/models/model';

@Injectable({
    providedIn: 'root'
})
export class ScreenRecordingService {
    private recordingState = new BehaviorSubject<RecordingState>({
        isRecording: false,
        recordingTime: 0,
        mediaRecorder: null,
        recordedChunks: []
    });

    private recordingInterval: any;
    private recordingCompleted = new Subject<RecordingCompleted>();
    private recordingCancelled = new Subject<void>();

    recordingState$ = this.recordingState.asObservable();
    recordingCompleted$ = this.recordingCompleted.asObservable();
    recordingCancelled$ = this.recordingCancelled.asObservable();

    async startRecording() {
        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                displaySurface: 'monitor',
                width: { ideal: 1920 },
                height: { ideal: 1080 }
            },
        });

        const mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'video/webm;codecs=vp9',
            videoBitsPerSecond: 2500000
        });

        const recordedChunks: Blob[] = [];

        mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                recordedChunks.push(event.data);
                this.recordingState.next({
                    ...this.recordingState.value,
                    recordedChunks
                });
            }
        };

        mediaRecorder.onstop = () => {
            this.handleRecordingComplete();
        };

        stream.getVideoTracks()[0].onended = () => {
            this.stopRecording();
        };

        mediaRecorder.start(1000);

        this.recordingState.next({
            isRecording: true,
            recordingTime: 0,
            mediaRecorder,
            recordedChunks
        });

        this.startTimer();
    }

    stopRecording() {
        const state = this.recordingState.value;

        if (state.mediaRecorder && state.isRecording) {
            state.mediaRecorder.stop();
            state.mediaRecorder.stream.getTracks().forEach(track => track.stop());
            this.stopTimer();

            this.recordingState.next({
                ...state,
                isRecording: false
            });
        } else if (state.mediaRecorder && !state.isRecording) {
            state.mediaRecorder.stream.getTracks().forEach(track => track.stop());
        }
    }

    cancelRecording() {
        const state = this.recordingState.value;

        if (state.mediaRecorder) {
            if (state.isRecording) {
                state.mediaRecorder.stop();
            }
            state.mediaRecorder.stream.getTracks().forEach(track => track.stop());
            this.stopTimer();
        }

        this.resetRecording();
        this.recordingCancelled.next();
    }

    private handleRecordingComplete() {
        const chunks = this.recordingState.value.recordedChunks;

        if (chunks.length === 0) {
            return;
        }

        const blob = new Blob(chunks, { type: 'video/webm' });
        const file = new File([blob], `recording-${Date.now()}.webm`, { type: 'video/webm' });
        const videoUrl = URL.createObjectURL(blob);

        this.recordingCompleted.next({ blob, file, videoUrl });
    }

    getRecordedBlob(): Blob | null {
        const chunks = this.recordingState.value.recordedChunks;
        if (chunks.length === 0) return null;
        return new Blob(chunks, { type: 'video/webm' });
    }

    resetRecording() {
        this.stopTimer();
        this.recordingState.next({
            isRecording: false,
            recordingTime: 0,
            mediaRecorder: null,
            recordedChunks: []
        });
    }

    private startTimer() {
        this.recordingInterval = setInterval(() => {
            const state = this.recordingState.value;
            this.recordingState.next({
                ...state,
                recordingTime: state.recordingTime + 1
            });
        }, 1000);
    }

    private stopTimer() {
        if (this.recordingInterval) {
            clearInterval(this.recordingInterval);
            this.recordingInterval = null;
        }
    }

    formatTime(seconds: number): string {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
}

